import React, { useEffect, useState, useMemo } from "react";
import Context from "../../Context";

import { Header } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import { TransferReviewModal } from "./orderInfo/components";
import {
  ArrowRightLeft,
  Search,
  Clock,
  CheckCircle2,
  XCircle,
  Phone,
  Eye,
  ExternalLink,
  Filter,
} from "lucide-react";

export const TransferRequestsPage = () => {
  const { navigate, callApi } = Context();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await callApi("orders-getTransferRequests", {
        status: "all",
        limit: 200,
      });
      if (res?.status && Array.isArray(res.data)) {
        setRequests(res.data);
      }
    } catch (err) {
      console.error("fetch OrderTransferRequests error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);


  // สถิติยอดรวม
  const stats = useMemo(() => {
    const pending = requests.filter((r) => r.status === "pending").length;
    const approved = requests.filter((r) => r.status === "approved").length;
    const rejected = requests.filter((r) => r.status === "rejected").length;
    return {
      total: requests.length,
      pending,
      approved,
      rejected,
    };
  }, [requests]);

  // Filter & Search
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      // Filter status
      if (selectedStatus !== "all" && r.status !== selectedStatus) {
        return false;
      }

      // Filter search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const orderIdMatch = (r.orderId || "").toLowerCase().includes(q);
        const shortIdMatch = (r.shortId || "").toLowerCase().includes(q);
        const fromPhone = (r.fromUser?.phone || "").includes(q);
        const toPhone = (r.toUser?.phone || "").includes(q);
        const fromName = `${r.fromUser?.firstName || ""} ${r.fromUser?.lastName || ""}`.toLowerCase().includes(q);
        const toName = `${r.toUser?.firstName || ""} ${r.toUser?.lastName || ""}`.toLowerCase().includes(q);
        const adminName = (r.requestedBy?.name || "").toLowerCase().includes(q);

        return (
          orderIdMatch ||
          shortIdMatch ||
          fromPhone ||
          toPhone ||
          fromName ||
          toName ||
          adminName
        );
      }

      return true;
    });
  }, [requests, selectedStatus, searchQuery]);

  const handleOpenReview = (request) => {
    setSelectedRequest(request);
    setIsReviewModalOpen(true);
  };

  return (
    <div className="h-fit w-full max-w-[1600px] mx-auto pb-10 min-w-0">
      {/* Header */}
      <Header
        header="Orders"
        subHeader="คำขอย้ายเจ้าของคำสั่งซื้อสลาก (Transfer Requests)"
        icon={<ArrowRightLeft className="text-blue" />}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {/* Pending */}
        <div
          onClick={() => setSelectedStatus("pending")}
          className={`p-4 rounded-md border transition-all cursor-pointer shadow-xs ${
            selectedStatus === "pending"
              ? "bg-amber-50 border-amber-400 ring-2 ring-amber-300"
              : "bg-white border-slate-200 hover:border-amber-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-amber-800 uppercase tracking-wider">
              รอดำเนินการอนุมัติ
            </span>
            <div className="p-1.5 bg-amber-100 text-amber-700 rounded-md">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-amber-900 mt-2 font-mono">
            {stats.pending.toLocaleString()}
          </p>
          <p className="text-[11px] text-amber-750 mt-0.5">
            รอ Dev / Super Admin ตรวจสอบ
          </p>
        </div>

        {/* Approved */}
        <div
          onClick={() => setSelectedStatus("approved")}
          className={`p-4 rounded-md border transition-all cursor-pointer shadow-xs ${
            selectedStatus === "approved"
              ? "bg-emerald-50 border-emerald-400 ring-2 ring-emerald-300"
              : "bg-white border-slate-200 hover:border-emerald-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
              อนุมัติแล้ว
            </span>
            <div className="p-1.5 bg-emerald-100 text-emerald-700 rounded-md">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-emerald-900 mt-2 font-mono">
            {stats.approved.toLocaleString()}
          </p>
          <p className="text-[11px] text-emerald-750 mt-0.5">
            โอนย้ายกรรมสิทธิ์สำเร็จ
          </p>
        </div>

        {/* Rejected */}
        <div
          onClick={() => setSelectedStatus("rejected")}
          className={`p-4 rounded-md border transition-all cursor-pointer shadow-xs ${
            selectedStatus === "rejected"
              ? "bg-rose-50 border-rose-400 ring-2 ring-rose-300"
              : "bg-white border-slate-200 hover:border-rose-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-rose-800 uppercase tracking-wider">
              ปฏิเสธคำขอ
            </span>
            <div className="p-1.5 bg-rose-100 text-rose-700 rounded-md">
              <XCircle className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-rose-900 mt-2 font-mono">
            {stats.rejected.toLocaleString()}
          </p>
          <p className="text-[11px] text-rose-750 mt-0.5">
            คำขอไม่ผ่านการตรวจสอบ
          </p>
        </div>

        {/* Total */}
        <div
          onClick={() => setSelectedStatus("all")}
          className={`p-4 rounded-md border transition-all cursor-pointer shadow-xs ${
            selectedStatus === "all"
              ? "bg-slate-100 border-slate-400 ring-2 ring-slate-300"
              : "bg-white border-slate-200 hover:border-slate-300"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
              คำขอทั้งหมด
            </span>
            <div className="p-1.5 bg-slate-100 text-slate-600 rounded-md">
              <ArrowRightLeft className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-extrabold text-slate-900 mt-2 font-mono">
            {stats.total.toLocaleString()}
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            รวมประวัติคำขอทั้งหมด
          </p>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white p-4 rounded-md border border-slate-200 shadow-xs mt-4 flex flex-wrap items-center justify-between gap-3">
        {/* Status Filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-bold text-slate-500 mr-1 flex items-center gap-1">
            <Filter className="w-3.5 h-3.5" /> ตัวกรอง:
          </span>
          {[
            { key: "all", label: "ทั้งหมด" },
            { key: "pending", label: "รอดำเนินการ", badge: stats.pending },
            { key: "approved", label: "อนุมัติแล้ว" },
            { key: "rejected", label: "ปฏิเสธ" },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setSelectedStatus(tab.key)}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition cursor-pointer flex items-center gap-1.5 ${
                selectedStatus === tab.key
                  ? "bg-navy text-white shadow-xs"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              <span>{tab.label}</span>
              {typeof tab.badge === "number" && tab.badge > 0 && (
                <span className="px-1.5 py-0.2 bg-rose-500 text-white rounded-md text-[10px] font-extrabold">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="w-full sm:w-80 relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            placeholder="ค้นหา Order ID, เบอร์โทร, หรือชื่อ..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-10 pl-9 pr-3 text-xs font-medium text-slate-800 bg-slate-50 border border-slate-300 rounded-md focus:outline-none focus:border-blue transition"
          />
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-md border border-slate-200 shadow-xs mt-4 overflow-hidden">
        {loading ? (
          <div className="p-16 flex flex-col items-center justify-center gap-3">
            <div className="w-8 h-8 border-4 border-navy border-t-transparent rounded-full animate-spin" />
            <p className="text-xs font-semibold text-slate-500">กำลังโหลดรายการคำขอ...</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-16 text-center text-slate-400 space-y-2">
            <ArrowRightLeft className="w-10 h-10 mx-auto stroke-1 text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">
              ไม่พบรายการคำขอย้ายคำสั่งซื้อ
            </p>
            <p className="text-xs text-slate-400">
              {searchQuery ? "ลองเปลี่ยนคำค้นหา หรือรีเซ็ตตัวกรองสถานะ" : "ยังไม่มีคำขอในหมวดหมู่นี้"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50/80 border-b border-slate-200">
                <TableRow>
                  <TableHead className="w-12 text-center text-xs font-bold text-slate-700 py-3">#</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 py-3">รหัสคำสั่งซื้อ</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 py-3">จากเจ้าของเดิม ➡️ ไปยังเจ้าของใหม่</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 py-3">สาเหตุ</TableHead>
                  <TableHead className="text-xs font-bold text-slate-700 py-3">ผู้ยื่นคำขอ</TableHead>
                  <TableHead className="text-center text-xs font-bold text-slate-700 py-3">สถานะ</TableHead>
                  <TableHead className="text-right text-xs font-bold text-slate-700 py-3 pr-4">จัดการ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map((req, idx) => {
                  const isPending = req.status === "pending";
                  const isApproved = req.status === "approved";
                  const isRejected = req.status === "rejected";

                  return (
                    <TableRow
                      key={req.id || idx}
                      className="hover:bg-slate-50/80 transition border-b border-slate-100 last:border-0"
                    >
                      {/* Index */}
                      <TableCell className="text-center font-mono text-xs text-slate-400 py-3">
                        {idx + 1}
                      </TableCell>

                      {/* Order Info */}
                      <TableCell className="py-3">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5">
                            <span
                              onClick={() => navigate(`/orders/${req.orderId}`)}
                              className="font-mono font-bold text-xs text-blue hover:underline cursor-pointer flex items-center gap-1"
                              title="เปิดดูคำสั่งซื้อ"
                            >
                              {req.orderId}
                              <ExternalLink className="w-3 h-3 opacity-60" />
                            </span>
                          </div>
                          <span className="text-[11px] text-slate-500 font-medium">
                            {req.roundDate ? `งวด ${req.roundDate}` : ""}
                            {req.totalPrice ? ` • ${req.totalPrice.toLocaleString()} บ.` : ""}
                          </span>
                        </div>
                      </TableCell>

                      {/* Before / After Users */}
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2 text-xs">
                          {/* From */}
                          <div className="min-w-0 max-w-[160px]">
                            <span className="text-[10px] text-slate-400 block font-semibold">จากเดิม:</span>
                            <span className="font-bold text-slate-800 truncate block">
                              {req.fromUser?.firstName
                                ? `${req.fromUser.firstName} ${req.fromUser.lastName || ""}`
                                : "ไม่ระบุชื่อ"}
                            </span>
                            <span className="text-[11px] text-slate-500 font-mono flex items-center gap-1">
                              <Phone className="w-2.5 h-2.5" />
                              {req.fromUser?.phone || "-"}
                            </span>
                          </div>

                          <ArrowRightLeft className="w-4 h-4 text-slate-300 shrink-0 mx-1" />

                          {/* To */}
                          <div className="min-w-0 max-w-[160px]">
                            <span className="text-[10px] text-blue block font-semibold">ไปยัง:</span>
                            <span className="font-bold text-slate-900 truncate block">
                              {req.toUser?.firstName
                                ? `${req.toUser.firstName} ${req.toUser.lastName || ""}`
                                : "ไม่ระบุชื่อ"}
                            </span>
                            <span className="text-[11px] text-blue/80 font-mono flex items-center gap-1">
                              <Phone className="w-2.5 h-2.5 text-blue" />
                              {req.toUser?.phone || "-"}
                            </span>
                          </div>
                        </div>
                      </TableCell>

                      {/* Reason */}
                      <TableCell className="py-3 max-w-[200px]">
                        <div className="flex flex-col">
                          <span className="text-xs font-medium text-slate-800 truncate" title={req.reason}>
                            {req.reason}
                          </span>
                          {req.detail && (
                            <span className="text-[11px] text-slate-400 truncate" title={req.detail}>
                              {req.detail}
                            </span>
                          )}
                          {Array.isArray(req.evidenceImages) && req.evidenceImages.length > 0 && (
                            <span className="text-[10px] text-blue font-bold mt-0.5">
                              📎 มีหลักฐาน {req.evidenceImages.length} รูป
                            </span>
                          )}
                        </div>
                      </TableCell>

                      {/* Requested By */}
                      <TableCell className="py-3">
                        <div className="flex flex-col text-xs">
                          <span className="font-semibold text-slate-700">
                            {req.requestedBy?.name || "แอดมิน"}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            {req.createdAt ? new Date(req.createdAt).toLocaleString("th-TH") : "-"}
                          </span>
                        </div>
                      </TableCell>

                      {/* Status */}
                      <TableCell className="text-center py-3">
                        {isPending && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200 shadow-2xs">
                            <Clock className="w-3.5 h-3.5" />
                            <span>รอดำเนินการ</span>
                          </span>
                        )}
                        {isApproved && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            <span>อนุมัติแล้ว</span>
                          </span>
                        )}
                        {isRejected && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200 shadow-2xs">
                            <XCircle className="w-3.5 h-3.5" />
                            <span>ปฏิเสธ</span>
                          </span>
                        )}
                      </TableCell>

                      {/* Action */}
                      <TableCell className="text-right py-3 pr-4">
                        <button
                          type="button"
                          onClick={() => handleOpenReview(req)}
                          className={`px-3 py-1.5 text-xs font-bold rounded-md transition shadow-xs inline-flex items-center gap-1.5 cursor-pointer ${
                            isPending
                              ? "bg-navy hover:bg-navy/90 text-white"
                              : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                          }`}
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>{isPending ? "ตรวจสอบ & อนุมัติ" : "ดูรายละเอียด"}</span>
                        </button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Review & Approval Modal */}
      {selectedRequest && (
        <TransferReviewModal
          isOpen={isReviewModalOpen}
          onClose={() => setIsReviewModalOpen(false)}
          request={selectedRequest}
          onSuccess={() => {
            setIsReviewModalOpen(false);
            fetchRequests();
          }}
        />

      )}
    </div>
  );
};

export default TransferRequestsPage;
